




















































































import Vue from 'vue';
import Component from 'vue-class-component';
import DatePicker from '@/project/ui-components/date-picker/DatePicker.vue';
import { AddressViewObject, DeliveryAddressViewObject, DeliveryMethodViewObject, DeliveryPriceViewModel } from '@/types/apiServerContract';
import { Prop, Watch } from 'vue-property-decorator';
import SpinnerOverlay from '@/project/spinners/SpinnerOverlay.vue';
import InputCheckbox from '@/project/form/InputCheckbox.vue';
import { ShippingData, Validatable } from '@/project/app/client-pages/checkout/types';
import Api from '@/project/http/Api.service';
import { debounce } from 'lodash';
import basketService from '@/project/basket/basket.service';
import AnimateValue from '@/core/ui-components/AnimateValue.vue';

@Component({
    components: { InputCheckbox, DatePicker, SpinnerOverlay, AnimateValue },
    model: {
        prop: 'shippingData',
        event: 'shippingDataUpdated'
    }
})
export default class CheckoutShipping extends Vue implements Validatable {
    selectedShipping: DeliveryMethodViewObject | null = null;
    postponeDelivery = false;
    deliveryDate: Date = new Date(this.minDate);
    deliveryPrices: DeliveryPriceViewModel[] = [];
    pricesFetched = false;

    // Debounce in order to avoid 4 incomplete parent-updates during initialization
    debouncedUpdateParent = debounce(this.updateParent, 50);
    debouncedFetchDeliveryPrices = debounce(this.fetchPrices, 50);
    validationError = false;

    @Prop(Array)
    shippingMethods!: DeliveryMethodViewObject[] | null;

    @Prop(Object)
    shippingData!: ShippingData | null;

    @Prop(Number)
    maxNumberOfShipments!: number;

    @Prop(Boolean)
    isPartDeliveryAllowed!: boolean;

    @Prop(Boolean)
    isPartDeliveryDefault!: boolean;

    @Prop(String)
    countryCode!: string | null;

    @Prop(Boolean)
    dataLoaded!: boolean;

    @Prop(Boolean)
    isErpAvailable!: boolean;

    @Prop(Object)
    deliveryAddress!: AddressViewObject | DeliveryAddressViewObject | null;

    isPartDelivery: boolean | null = null;

    @Watch('isPartDeliveryAllowed')
    onIsPartDeliveryAllowedChange() {
        this.isPartDelivery = basketService.basket!.isPartDelivery ?? this.isPartDeliveryDefault;
    }

    @Watch('shippingData')
    onShippingDataChange(value: ShippingData | undefined) {
        this.selectedShipping = this.shippingMethods?.find(sm => sm.shippingMethodCode === value?.shippingMethodCode) ?? null;
        this.deliveryDate = value?.postponeShipmentDate ?? new Date(this.minDate);
        this.postponeDelivery = value?.postponeDelivery ?? false;

        if (this.dataLoaded) {
            this.isPartDelivery = value?.isPartDelivery ?? this.isPartDeliveryDefault;
        }
    }

    @Watch('selectedShipping')
    onShippingChange() {
        this.debouncedUpdateParent();
    }

    @Watch('isPartDelivery')
    onIsPartDeliveryChange() {
        this.debouncedUpdateParent();
    }

    @Watch('postponeDelivery')
    onPostponeDeliveryChange(value) {
        if (value && !this.deliveryDate) {
            this.deliveryDate = new Date(this.minDate);
        }
        this.debouncedUpdateParent();
    }

    @Watch('deliveryDate')
    onDeliveryDateChange() {
        this.debouncedUpdateParent();
    }

    @Watch('countryCode', {
        immediate: true
    })
    onCountryCodeChange() {
        this.debouncedFetchDeliveryPrices();
    }

    @Watch('deliveryAddress')
    onDeliveryAddressChange(newAddress, oldAddress) {
        this.debouncedFetchDeliveryPrices();
    }

    get shippingMethodsSorted() {
        return this.deliveryPrices?.map(deliveryPrice => {
            return this.shippingMethods?.find(method => method.shippingMethodCode === deliveryPrice.shippingMethodCode);
        });
    }

    async fetchPrices() {
        if (!this.countryCode) return;
        try {
            this.deliveryPrices = await Api.delivery.prices(this.countryCode, this.deliveryAddress?.code, this.deliveryAddress?.postalCode);
        } catch {
        } finally {
            this.pricesFetched = true;
        }
    }

    shippingPrice(shipmentCode: string): string | null {
        const deliveryPrice = this.deliveryPrices.find(dp => dp.shippingMethodCode === shipmentCode && dp.isServiceAvailable);
        return deliveryPrice?.price.cultureFormatted ?? null;
    }

    updateParent() {
        this.validationError = false;
        this.$emit('shippingDataUpdated', {
            isPartDelivery: this.isPartDelivery,
            postponeDelivery: this.postponeDelivery,
            postponeShipmentDate: this.deliveryDate,
            shippingMethodCode: this.selectedShipping?.shippingMethodCode,
            deliveryPhoneNumberRequired: this.selectedShipping?.shipmentPhoneMandatory ?? false,
            deliveryEmailRequired: this.selectedShipping?.shipmentEmailMandatory ?? false
        } as ShippingData);
    }

    get minDate(): Date {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow;
    }

    validate(): Promise<boolean> {
        const valid = this.selectedShipping !== null;
        this.validationError = !valid;
        return Promise.resolve(valid);
    }

    isShippingMethodDisabled(method: string) {
        return basketService.basket!.shippingMethodLocked && method !== this.selectedShipping?.shippingMethodCode;
    }

    get orderContainsQuotes() {
        return basketService.basket!.quotes.length > 0;
    }

    hasShippingOptions() {
        this.$emit('update:shipping', this.shippingMethods && this.deliveryPrices && this.deliveryPrices.length > 0);
        return this.shippingMethods && this.deliveryPrices && this.deliveryPrices.length > 0;
    }

    created() {
        this.hasShippingOptions();
    }
}
